// ./src/components/features/Users/UserSelector/UserSelector.tsx
import React, { useMemo, useCallback, useEffect, useState } from "react";
import {
  Select,
  SelectProps,
  Group,
  Text,
  Stack,
  Box,
  Avatar,
  ComboboxProps,
  TextInput,
  CloseButton,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { User } from "@/utils/api";
import { useUsers } from "@/hooks/useUsers";
import { useAppEnvironment } from "@/contexts";
import { LoadingIndicator, AppIcon } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { UserPickerModal } from "./UserPickerModal";

// ============================================
// Types
// ============================================

export interface UserSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange" | "name"> {
  /** Field name for form binding */
  name?: string;

  /** Currently selected user ID */
  value?: string | null | undefined;

  /** Callback fired when user selection changes: (name, value, user?) */
  onChange?: (name: string, value: string | null, user?: User) => void;

  /** Placeholder text when no user is selected */
  placeholder?: string;

  /** Filter users by role IDs (UUID array) - only show users with matching roles */
  roleIds?: string[];

  /** Filter users by status (integer array) - only show users with matching statuses */
  statuses?: number[];

  /** Custom filter function to filter users */
  userFilter?: (user: User) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Placeholder text shown while loading */
  loadingPlaceholder?: string;

  /** Text shown when no users are available */
  emptyText?: string;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether to show user details (email) in dropdown */
  showDetails?: boolean;

  /** Label for the field */
  label?: string;

  /** Whether to show "All Users" option */
  showAllOption?: boolean;

  /** Label for "All Users" option */
  allOptionLabel?: string;

  /** Props passed to the underlying Combobox (e.g., withinPortal) */
  comboboxProps?: ComboboxProps;

  /** Current user ID for loading users - required for useUsers hook */
  userId?: string | null;

  /** Whether to auto-load users on mount */
  autoLoad?: boolean;
}

// ============================================
// Option Data Interface
// ============================================

interface UserOptionData {
  value: string;
  label: string;
  details?: string;
  avatarUrl?: string;
  disabled?: boolean;
}

// ============================================
// Constants
// ============================================

const ALL_USERS_VALUE = "__all__";

// ============================================
// Helper Functions
// ============================================

/**
 * Get display name for a user
 */
const getUserDisplayName = (user: User): string => {
  const nameParts = [user.firstName, user.middleName, user.lastName].filter(
    Boolean
  );

  if (nameParts.length > 0) {
    return nameParts.join(" ");
  }

  return user.username || user.email || "Unknown User";
};

/**
 * Get user initials for avatar fallback
 */
const getUserInitials = (user: User): string => {
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }
  if (user.firstName) {
    return user.firstName.substring(0, 2).toUpperCase();
  }
  if (user.username) {
    return user.username.substring(0, 2).toUpperCase();
  }
  if (user.email) {
    return user.email.substring(0, 2).toUpperCase();
  }
  return "??";
};

/**
 * Check if user has any of the specified role IDs
 */
const userHasRole = (user: User, roleIds: string[]): boolean => {
  if (!roleIds.length) return true;
  if (!user.userAccounts?.length) return false;

  return user.userAccounts.some(
    (ua) => ua.userRoleId && roleIds.includes(ua.userRoleId)
  );
};

/**
 * Check if user has any of the specified statuses
 */
const userHasStatus = (user: User, statuses: number[]): boolean => {
  if (!statuses.length) return true;
  if (user.status === null || user.status === undefined) return false;

  return statuses.includes(user.status);
};

// ============================================
// Component
// ============================================

const UserSelectorComponent: React.FC<UserSelectorProps> = ({
  name = "userId",
  value,
  onChange,
  placeholder,
  roleIds = [],
  statuses = [],
  userFilter,
  required = false,
  loadingPlaceholder,
  emptyText,
  error,
  disabled,
  size = "md",
  showDetails = true,
  label,
  showAllOption = false,
  allOptionLabel,
  comboboxProps,
  userId = null,
  autoLoad = true,
  ...selectProps
}) => {
  const { t } = useTranslation(["users", "common"]);
  const { isMobile } = useAppEnvironment();

  // Use the users hook to load and manage users
  const { users, loading, lastError, getUserById } = useUsers({
    autoLoad,
    initialFilters: {},
  });

  // Modal state (mobile)
  const [modalOpened, setModalOpened] = useState(false);

  // Reload users when roleIds or statuses change (if needed)
  useEffect(() => {
    // If we need to refetch based on filter changes, we could do it here
    // For now, we filter client-side after loading all users
  }, [roleIds, statuses]);

  // Default translations
  const defaultPlaceholder =
    placeholder || t("users:selectors.user.placeholder", "Select user");
  const defaultLabel = label;
  const defaultLoadingPlaceholder =
    loadingPlaceholder || t("users:selectors.user.loading", "Loading users...");
  const defaultEmptyText =
    emptyText || t("users:selectors.user.empty", "No users available");
  const defaultAllOptionLabel =
    allOptionLabel || t("users:selectors.user.allUsers", "All Users");

  // Filter users based on roleIds and statuses
  const filteredUsers = useMemo(() => {
    let result = users;

    // Filter by role IDs
    if (roleIds.length > 0) {
      result = result.filter((user) => userHasRole(user, roleIds));
    }

    // Filter by statuses
    if (statuses.length > 0) {
      result = result.filter((user) => userHasStatus(user, statuses));
    }

    // Apply custom filter if provided
    if (userFilter) {
      result = result.filter(userFilter);
    }

    return result;
  }, [users, roleIds, statuses, userFilter]);

  // Create a map for quick user lookup by ID
  const userMap = useMemo(() => {
    const map = new Map<string, User>();
    filteredUsers.forEach((user) => {
      if (user.id) {
        map.set(user.id, user);
      }
    });
    return map;
  }, [filteredUsers]);

  // Prepare user options for the select (desktop)
  const userOptions = useMemo((): UserOptionData[] => {
    const options: UserOptionData[] = [];

    // Add "All Users" option if enabled
    if (showAllOption) {
      options.push({
        value: ALL_USERS_VALUE,
        label: defaultAllOptionLabel,
        details: undefined,
        avatarUrl: undefined,
      });
    }

    // Add user options
    filteredUsers.forEach((user) => {
      if (user.id) {
        options.push({
          value: user.id,
          label: getUserDisplayName(user),
          details: showDetails ? user.email || undefined : undefined,
          avatarUrl: user.avatarUrl || undefined,
        });
      }
    });

    return options;
  }, [filteredUsers, showAllOption, defaultAllOptionLabel, showDetails]);

  // Handle onChange with user object lookup (desktop)
  const handleChange = useCallback(
    (selectedValue: string | null) => {
      if (selectedValue === ALL_USERS_VALUE) {
        onChange?.(name, null, undefined);
      } else if (selectedValue) {
        const user = userMap.get(selectedValue) || getUserById(selectedValue);
        onChange?.(name, selectedValue, user);
      } else {
        onChange?.(name, null, undefined);
      }
    },
    [name, userMap, getUserById, onChange]
  );

  // Handle modal selection (mobile)
  const handleModalSelect = useCallback(
    (userId: string | null, user?: User) => {
      onChange?.(name, userId, user);
    },
    [name, onChange]
  );

  // Handle clear (mobile)
  const handleClear = useCallback(() => {
    onChange?.(name, null, undefined);
  }, [name, onChange]);

  // Get display text for the selected user
  const selectedDisplayText = useMemo(() => {
    if (value === null && showAllOption) return defaultAllOptionLabel;
    if (!value) return "";
    const user = userMap.get(value) || getUserById(value);
    if (!user) return "";
    return getUserDisplayName(user);
  }, [value, showAllOption, defaultAllOptionLabel, userMap, getUserById]);

  // Render option for dropdown (desktop)
  const renderOption: SelectProps["renderOption"] = useCallback(
    ({ option }: any) => {
      const optionData = option as UserOptionData;

      // Special rendering for "All Users"
      if (optionData.value === ALL_USERS_VALUE) {
        return (
          <Group gap="sm" wrap="nowrap">
            <AppIcon iconName="Users" size={24} />
            <Text size="md" fw={500}>
              {optionData.label}
            </Text>
          </Group>
        );
      }

      // Get user for initials
      const user = userMap.get(optionData.value);
      const initials = user ? getUserInitials(user) : "??";

      return (
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <Box pt={2}>
            <Avatar
              src={optionData.avatarUrl}
              size={32}
              radius="xl"
              color="brand"
            >
              {initials}
            </Avatar>
          </Box>
          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={500} lineClamp={1}>
              {optionData.label}
            </Text>
            {optionData.details && (
              <Text size="sm" c="dimmed" lineClamp={1}>
                {optionData.details}
              </Text>
            )}
          </Stack>
        </Group>
      );
    },
    [userMap]
  );

  // Memoize left section icon
  const leftSection = useMemo(() => {
    if (value === ALL_USERS_VALUE || !value) {
      return <AppIcon iconName="User" size={20} />;
    }

    // Show avatar for selected user
    const selectedUser = userMap.get(value);
    if (selectedUser) {
      const initials = getUserInitials(selectedUser);
      return (
        <Avatar
          src={selectedUser.avatarUrl}
          size={24}
          radius="xl"
          color="brand"
        >
          {initials}
        </Avatar>
      );
    }

    return <AppIcon iconName="User" size={20} />;
  }, [value, userMap]);

  // Determine current state
  const isEmpty = !loading && filteredUsers.length === 0;

  // ==========================================
  // Mobile: Read-only input + full-screen modal
  // ==========================================
  if (isMobile) {
    const isDisabled =
      disabled || (loading && users.length === 0) || (isEmpty && !showAllOption);

    const mobilePlaceholder =
      loading && users.length === 0
        ? defaultLoadingPlaceholder
        : isEmpty && !showAllOption
          ? defaultEmptyText
          : defaultPlaceholder;

    const mobileRightSection = (() => {
      if (loading && users.length === 0) {
        return <LoadingIndicator size={size} color="brand" />;
      }
      if (selectedDisplayText && !required && !disabled) {
        return (
          <CloseButton
            size={size}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            aria-label={t("common:form.clearSelection")}
          />
        );
      }
      return <AppIcon iconName="ChevronDown" size={18} />;
    })();

    return (
      <Box>
        <TextInput
          label={defaultLabel}
          placeholder={mobilePlaceholder}
          value={selectedDisplayText}
          readOnly
          onClick={() => {
            if (!isDisabled) {
              setModalOpened(true);
            }
          }}
          leftSection={leftSection}
          rightSection={mobileRightSection}
          rightSectionPointerEvents={
            selectedDisplayText && !required && !disabled ? "auto" : "none"
          }
          error={
            lastError && !loading && users.length === 0
              ? error || t("common:messages.loadError")
              : error
          }
          required={required}
          disabled={isDisabled}
          size={size}
          styles={{
            input: {
              cursor: isDisabled ? "not-allowed" : "pointer",
              caretColor: "transparent",
            },
          }}
        />

        <UserPickerModal
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          value={value}
          onSelect={handleModalSelect}
          users={filteredUsers}
          showAllOption={showAllOption}
          allOptionLabel={defaultAllOptionLabel}
          showDetails={showDetails}
        />
      </Box>
    );
  }

  // ==========================================
  // Desktop: Original Select behavior
  // ==========================================

  // Memoize select state
  const selectState = useMemo(() => {
    if (loading && users.length === 0) {
      return {
        data: [] as UserOptionData[],
        placeholder: defaultLoadingPlaceholder,
        isDisabled: true,
        showLoading: true,
        errorMessage: undefined as string | undefined,
      };
    }

    if (lastError && !loading && users.length === 0) {
      return {
        data: [] as UserOptionData[],
        placeholder: t("common:errors.generic", "Error loading data"),
        isDisabled: true,
        showLoading: false,
        errorMessage: error || t("common:messages.loadError", "Failed to load"),
      };
    }

    if (isEmpty && !showAllOption) {
      return {
        data: [] as UserOptionData[],
        placeholder: defaultEmptyText,
        isDisabled: true,
        showLoading: false,
        errorMessage: undefined as string | undefined,
      };
    }

    return {
      data: userOptions,
      placeholder: defaultPlaceholder,
      isDisabled: disabled || loading,
      showLoading: loading && users.length > 0,
      errorMessage: undefined as string | undefined,
    };
  }, [
    loading,
    users.length,
    lastError,
    isEmpty,
    showAllOption,
    userOptions,
    defaultLoadingPlaceholder,
    defaultEmptyText,
    defaultPlaceholder,
    disabled,
    error,
    t,
  ]);

  // Memoize right section
  const rightSection = useMemo(() => {
    if (selectState.showLoading) {
      return <LoadingIndicator size={size} color="brand" />;
    }
    return selectProps.rightSection;
  }, [selectState.showLoading, size, selectProps.rightSection]);

  // Determine display value (handle "All Users" case)
  const displayValue = useMemo(() => {
    if (value === null && showAllOption) {
      return ALL_USERS_VALUE;
    }
    return value ?? null;
  }, [value, showAllOption]);

  return (
    <Box>
      <Select
        {...selectProps}
        label={defaultLabel}
        size={size}
        value={displayValue}
        onChange={handleChange}
        placeholder={selectState.placeholder}
        clearable={!required && !showAllOption}
        required={required}
        allowDeselect={!required}
        disabled={selectState.isDisabled}
        error={selectState.errorMessage || error}
        data={selectState.data}
        rightSection={rightSection}
        leftSection={leftSection}
        renderOption={renderOption}
        nothingFoundMessage={t("common:form.noOptions", "No options")}
        comboboxProps={comboboxProps ?? DRAWER_COMBOBOX_PROPS}
        searchable
      />
    </Box>
  );
};

export const UserSelector = React.memo(UserSelectorComponent);